import { useQuery } from "@tanstack/react-query";
import { createFileRoute, redirect, useSearch } from "@tanstack/react-router";
import { motion } from "framer-motion";
import { useMemo, useState } from "react";

import { ConnectGSC } from "@/components/keywords/ConnectGSC";
import { FiltersBar, type Filters } from "@/components/keywords/FiltersBar";
import { KeywordsTable } from "@/components/keywords/KeywordsTable";
import { api, type GscKeywordRow } from "@/lib/api";
import { formatRelativeTime } from "@/lib/formatters";
import { isAuthenticated } from "@/lib/pb";

interface KeywordsSearch {
  connected?: string;
}

export const Route = createFileRoute("/keywords/")({
  validateSearch: (search: Record<string, unknown>): KeywordsSearch => ({
    connected: search.connected as string | undefined,
  }),
  beforeLoad: () => {
    if (!isAuthenticated()) throw redirect({ to: "/login" });
  },
  component: KeywordsPage,
});

// Default MIN IMPR is intentionally low (20) because thinkler.ai is early-stage:
// most queries have modest impressions. Bump this up once the site consistently
// pulls 3-digit impressions on a broad set of queries.
const DEFAULT_FILTERS: Filters = {
  months: 6,
  strikingOnly: true,
  posMin: 11,
  posMax: 30,
  minImpressions: 20,
  sort: "impressions",
};

function KeywordsPage() {
  const search = useSearch({ from: "/keywords/" });
  const justConnected = search.connected === "1";

  const [filters, setFilters] = useState<Filters>(DEFAULT_FILTERS);
  const [selectedSite, setSelectedSite] = useState<string | null>(null);
  const [forceRefresh, setForceRefresh] = useState(false);

  const statusQuery = useQuery({
    queryKey: ["gsc-status"],
    queryFn: () => api.gsc.status(),
    // Poll once a bit after connect so the redirect state is picked up.
    refetchInterval: justConnected ? 1500 : false,
  });

  const sitesQuery = useQuery({
    queryKey: ["gsc-sites"],
    queryFn: () => api.gsc.sites(),
    enabled: !!statusQuery.data?.connected,
  });

  const sites = sitesQuery.data?.sites ?? [];
  const effectiveSite = selectedSite ?? sites[0]?.siteUrl ?? null;

  const keywordsQuery = useQuery({
    queryKey: ["gsc-keywords", effectiveSite, filters.months, forceRefresh],
    queryFn: () =>
      api.gsc.keywords({
        site: effectiveSite!,
        months: filters.months,
        // Fetch striking-only from backend so we send less over the wire; UI
        // filters (pos range, min impressions) apply client-side over that.
        strikingOnly: false,
        forceRefresh,
      }),
    enabled: !!effectiveSite,
  });

  const filteredRows = useMemo<GscKeywordRow[]>(() => {
    const all = keywordsQuery.data?.rows ?? [];
    let out = all.filter(
      (r) =>
        r.position >= filters.posMin &&
        r.position <= filters.posMax &&
        r.impressions >= filters.minImpressions,
    );
    if (filters.strikingOnly) out = out.filter((r) => r.striking_distance);

    const cmp = {
      impressions: (a: GscKeywordRow, b: GscKeywordRow) => b.impressions - a.impressions,
      position:    (a: GscKeywordRow, b: GscKeywordRow) => a.position - b.position,
      ctr:         (a: GscKeywordRow, b: GscKeywordRow) => a.ctr - b.ctr,
    }[filters.sort];
    return [...out].sort(cmp);
  }, [keywordsQuery.data, filters]);

  const strikingCount = useMemo(
    () => (keywordsQuery.data?.rows ?? []).filter((r) => r.striking_distance).length,
    [keywordsQuery.data],
  );

  // ─── render ─────────────────────────────────────────────────────────
  if (statusQuery.isLoading) return <Skeleton label="loading gsc status" />;

  if (!statusQuery.data?.connected) return <Shell><ConnectGSC /></Shell>;

  if (sitesQuery.isLoading) return <Skeleton label="loading verified properties" />;

  if (sites.length === 0) return <Shell><ConnectGSC reason="no-sites" /></Shell>;

  return (
    <Shell>
      <Header
        siteCount={sites.length}
        totalCount={keywordsQuery.data?.count ?? 0}
        strikingCount={strikingCount}
        fetchedAt={keywordsQuery.data?.fetched_at ?? null}
        cached={keywordsQuery.data?.cached ?? false}
      />

      {sites.length > 1 && (
        <div className="surface p-3 flex items-center gap-3">
          <span className="path-chip">property</span>
          <select
            value={effectiveSite ?? ""}
            onChange={(e) => setSelectedSite(e.target.value)}
            className="flex-1 px-2 py-1 bg-bg-elev border border-border rounded-sm text-text font-mono text-sm"
          >
            {sites.map((s) => (
              <option key={s.siteUrl} value={s.siteUrl}>
                {s.siteUrl}
              </option>
            ))}
          </select>
        </div>
      )}

      <FiltersBar
        value={filters}
        onChange={setFilters}
        onRefresh={() => {
          setForceRefresh((v) => !v);
        }}
        refreshing={keywordsQuery.isFetching}
      />

      {keywordsQuery.isLoading ? (
        <Skeleton label="fetching search console data" />
      ) : keywordsQuery.error ? (
        <div className="surface p-6 text-danger font-mono text-sm">
          {(keywordsQuery.error as Error).message}
        </div>
      ) : (
        <KeywordsTable
          site={effectiveSite!}
          rows={filteredRows}
          onGenerated={() => keywordsQuery.refetch()}
        />
      )}
    </Shell>
  );
}

// ─── layout helpers ──────────────────────────────────────────────────
function Shell({ children }: { children: React.ReactNode }) {
  return <div className="max-w-6xl mx-auto space-y-6">{children}</div>;
}

function Header({
  siteCount,
  totalCount,
  strikingCount,
  fetchedAt,
  cached,
}: {
  siteCount: number;
  totalCount: number;
  strikingCount: number;
  fetchedAt: string | null;
  cached: boolean;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
    >
      <p className="path-chip">/keywords · el portero</p>
      <div className="flex items-baseline gap-3 mt-1">
        <h1 className="text-3xl font-semibold">Portero GSC</h1>
        <span className="font-mono text-sm text-muted">
          [{siteCount} {siteCount === 1 ? "site" : "sites"} · {totalCount} queries · {strikingCount} striking]
        </span>
      </div>
      {fetchedAt && (
        <p className="mt-1 font-mono text-xs text-muted/60">
          {cached ? "cached" : "fresh"} · {formatRelativeTime(fetchedAt)}
        </p>
      )}
    </motion.div>
  );
}

function Skeleton({ label }: { label: string }) {
  return (
    <Shell>
      <div className="surface p-12 text-center">
        <p className="font-mono text-sm text-muted">$ {label}…</p>
      </div>
    </Shell>
  );
}
